
import { defineComponent, onMounted, ref, computed } from "vue";
import { ErrorMessage, Field, Form } from "vee-validate";
import { setCurrentPageBreadcrumbs } from "@/core/helpers/breadcrumb";
import { Actions } from "@/store/enums/StoreEnums";
import { useStore } from "vuex";
import Swal from "sweetalert2/dist/sweetalert2.js";
import * as Yup from "yup";
import { values } from "lodash";

interface ProfileDetails {
    tokenUsuario: string;
    nombres: string;
    apellidos: string;
    email: string;
    telefono: Int16Array;
    bancoId: string;
    nombreBanco: string;
    tipoCuenta: string;
    numeroCuenta: string;
}

interface ProfileSecurity {
    tokenUsuario: string;
    contrasena: string;
    nuevaContrasena: string;
    confirmacionNuevaContrasena: string;
}

export default defineComponent({
    name: "profile-settings",
    components: {
        ErrorMessage,
        Field,
        Form,
    },
    setup() {
        const submitButton1 = ref<HTMLElement | null>(null);
        const submitButton2 = ref<HTMLElement | null>(null);
        const submitButton3 = ref<HTMLElement | null>(null);
        const submitButton4 = ref<HTMLElement | null>(null);
        const submitButton5 = ref<HTMLElement | null>(null);
        const submitButtonDatosBancarios = ref<HTMLElement | null>(null);
        const submitButtonEnviarDocumentoBanco = ref<HTMLElement | null>(null);
        const updateEmailButton = ref<HTMLElement | null>(null);
        const updatePasswordButton = ref<HTMLElement | null>(null);

        const emailFormDisplay = ref(false);
        const passwordFormDisplay = ref(false);
        const documento = ref(null);
        const cargarDocumento = async (e) => {
            try {
                const documentoCargado = e.target.files;

                if (documentoCargado.length > 0) {
                    const file = documentoCargado[0];

                    const extensionPermitida = ["pdf"];
                    const extension = file.name.split(".").pop()
                        ? file.name.split(".").pop().toLowerCase()
                        : "";

                    if (!extensionPermitida.includes(extension)) {
                        return;
                    }

                    documento.value = file;
                }
            } catch (error) {
                console.error(error);
            }
        };

        const descargarDocumento = async (item) => {
            try {
                const resul = await store.dispatch(
                    Actions.DESCARGAR_LINK_DOCUMENTO,
                    item
                );

                const url = `data:${resul.ContentType};base64,${resul.contenido}`;
                const descargar = document.createElement("a");
                const nombreDocumento = resul.nombre;

                descargar.href = url;
                descargar.download = nombreDocumento;
                descargar.click();
            } catch (error) {
                console.error(error);
            }
        };

        const enviarDocumento = async () => {
            try {
                if (documento.value !== null) {
                    submitButtonEnviarDocumentoBanco.value.setAttribute(
                        "data-kt-indicator",
                        "on"
                    );

                    const formData = new FormData();
                    formData.append("file", documento.value);
                    formData.append(
                        "proveedor",
                        JSON.stringify(store.getters.currentUser)
                    );
                    loading.value = true;
                    const resul = await store.dispatch(
                        Actions.SUBIR_DOCUMENTO_BANCO,
                        formData
                    );
                    if (resul.status === 204) {
                        loading.value = false;
                        submitButtonEnviarDocumentoBanco.value?.removeAttribute(
                            "data-kt-indicator"
                        );

                        Swal.fire({
                            text: "Documento cargando correctamente!!!",
                            icon: "success",
                            buttonsStyling: false,
                            confirmButtonText: "Ok",
                            header: "background: #006C90; height:300px",
                            customClass: {
                                confirmButton: "btn btn-melon",
                            },
                        });
                        documento.value = null;
                    } else {
                        loading.value = false;
                        submitButtonEnviarDocumentoBanco.value?.removeAttribute(
                            "data-kt-indicator"
                        );

                        Swal.fire({
                            text: "Ha ocurrido un error al cargar el documento, intente nuevamente",
                            icon: "error",
                            buttonsStyling: false,
                            confirmButtonText: "Intentar Nuevamente",
                            customClass: {
                                confirmButton: "btn btn-melon",
                            },
                        });
                    }
                    loading.value = false;
                    submitButtonEnviarDocumentoBanco.value?.removeAttribute(
                        "data-kt-indicator"
                    );
                }
            } catch (error) {
                console.error(error);
                loading.value = false;
                submitButtonEnviarDocumentoBanco.value?.removeAttribute(
                    "data-kt-indicator"
                );

                Swal.fire({
                    text: "Ha ocurrido un error al cargar el documento, intente nuevamente",
                    icon: "error",
                    buttonsStyling: false,
                    confirmButtonText: "Intentar Nuevamente",
                    customClass: {
                        confirmButton: "btn btn-melon",
                    },
                });
            }
        };

        const profileDetailsValidator = Yup.object().shape({
            nombres: Yup.string().required("Es obligatorio").label("Nombre"),
            apellidos: Yup.string()
                .required("Es obligatorio")
                .label("Apellido"),
            telefono: Yup.string().required("Es obligatorio").label("Teléfono"),
        });

        const changeEmail = Yup.object().shape({
            emailaddress: Yup.string().required().email().label("Email"),
            confirmemailpassword: Yup.string().required().label("Password"),
        });

        const changePassword = Yup.object().shape({
            contrasena: Yup.string()
                .required("Es obligatorio")
                .label("Contraseña"),
            nuevaContrasena: Yup.string()
                .required("Es obligatorio")
                .min(8, "Por favor ingresa al menos 8 caracteres")
                .max(100, "La contraseña no puede tener más de 100 caracteres")
                .matches(
                    /[!#%&'()*+,-./:;<=>?@[\\\]^_`{|}~]/,
                    "Debe contener al menos un carácter especial"
                )
                .matches(/[a-z]/, "Debe contener al menos una letra minúscula")
                .matches(/[A-Z]/, "Debe contener al menos una letra mayúscula")
                .matches(/\d/, "Debe contener al menos un número")
                .label("Nueva Contraseña"),
            confirmacionNuevaContrasena: Yup.string()
                .required("Es obligatorio")
                .oneOf(
                    [Yup.ref("nuevaContrasena"), null],
                    "La confirmación debe coincidir con la nueva"
                )
                .label("Confirmación de contraseña"),
        });

        const saveChanges1 = () => {
            if (submitButton1.value) {
                // Activate indicator
                submitButton1.value.setAttribute("data-kt-indicator", "on");
                store
                    .dispatch(
                        Actions.UPD_USUARIO_DATOS_BASE,
                        profileDetails.value
                    )
                    .then(() => {
                        loading = ref(false);
                        submitButton1.value?.removeAttribute(
                            "data-kt-indicator"
                        );
                        Swal.fire({
                            text: "Su información ha sido actualizada correctamente.",
                            icon: "success",
                            confirmButtonText: "Ok",
                            buttonsStyling: false,
                            customClass: {
                                confirmButton: "btn btn-light-primary",
                            },
                        });
                    })
                    .catch(() => {
                        const [error] = Object.keys(
                            store.getters.getUsuarioErrors
                        );
                        Swal.fire({
                            text: store.getters.getUsuarioErrors[error],
                            icon: "error",
                            confirmButtonText: "Ok",
                            buttonsStyling: false,
                            customClass: {
                                confirmButton: "btn btn-light-primary",
                            },
                        });
                    });
            }
        };

        const confirmarSaveDatosBancarios = () => {
            Swal.fire({
                title: "Confirmar Cambios",
                text: "Esta seguro que los datos estanb correctos?",
                icon: "warning",
                showCancelButton: true,
                confirmButtonColor: "#00526d",
                cancelButtonColor: "#93becd",
                confirmButtonText: "Si, estoy seguro!!",
                cancelButtonText: "Cancelar",
            }).then((result) => {
                if (result.isConfirmed) {
                    saveDatosBancarios()
                }
            });
        };
        const saveDatosBancarios = () => {
            if (submitButtonDatosBancarios.value) {
                // Activate indicator
                submitButtonDatosBancarios.value.setAttribute(
                    "data-kt-indicator",
                    "on"
                );
                store
                    .dispatch(Actions.UPD_PROVEEDOR, datosProveedor.value)
                    .then(() => {
                        loading = ref(false);
                        getProveedor();
                        submitButtonDatosBancarios.value?.removeAttribute(
                            "data-kt-indicator"
                        );
                        Swal.fire({
                            text: "Su información ha sido actualizada correctamente.",
                            icon: "success",
                            confirmButtonText: "Ok",
                            buttonsStyling: false,
                            customClass: {
                                confirmButton: "btn btn-light-primary",
                            },
                        });
                    })
                    .catch(() => {
                        const [error] = Object.keys(
                            store.getters.getProveedorErrors
                        );
                        Swal.fire({
                            text: store.getters.getProveedorErrors[error],
                            icon: "error",
                            confirmButtonText: "Ok",
                            buttonsStyling: false,
                            customClass: {
                                confirmButton: "btn btn-light-primary",
                            },
                        });
                    });
            }
        };

        const updateEmail = () => {
            if (updateEmailButton.value) {
                // Activate indicator
                updateEmailButton.value.setAttribute("data-kt-indicator", "on");

                setTimeout(() => {
                    updateEmailButton.value?.removeAttribute(
                        "data-kt-indicator"
                    );

                    emailFormDisplay.value = false;
                }, 2000);
            }
        };

        const updatePassword = () => {
            if (updatePasswordButton.value) {
                // Activate indicator
                updatePasswordButton.value.setAttribute(
                    "data-kt-indicator",
                    "on"
                );

                setTimeout(() => {
                    updatePasswordButton.value?.removeAttribute(
                        "data-kt-indicator"
                    );
                    store
                        .dispatch(
                            Actions.UPD_CONTRASENA_USUARIO,
                            profileSecurity.value
                        )
                        .then(() => {
                            loading = ref(false);
                            submitButton1.value?.removeAttribute(
                                "data-kt-indicator"
                            );
                            Swal.fire({
                                text: "Su contraseña ha sido actualizada correctamente.",
                                icon: "success",
                                confirmButtonText: "Ok",
                                buttonsStyling: false,
                                customClass: {
                                    confirmButton: "btn btn-light-primary",
                                },
                            }).then(() => {
                                passwordFormDisplay.value = false;
                            });
                        })
                        .catch(() => {
                            const [error] = Object.keys(
                                store.getters.getUsuarioErrors
                            );
                            Swal.fire({
                                text: store.getters.getUsuarioErrors[error],
                                icon: "error",
                                confirmButtonText: "Ok",
                                buttonsStyling: false,
                                customClass: {
                                    confirmButton: "btn btn-light-primary",
                                },
                            });
                        });
                }, 0);
            }
        };
        const store = useStore();
        const bancos = computed(() => {
            return store.getters.allBancos;
        });

        const datosProveedor = computed(() => {
            return store.getters.currentProveedor;
        });

        // const bancos = ref([]);
        const getBancos = () => {
            store
                .dispatch(Actions.GET_BANCOS)
                .then(() => {
                    loading = ref(false);
                })
                .catch(() => {
                    const [error] = Object.keys(store.getters.getBancoErrors);
                });
        };

        const getProveedor = () => {
            store
                .dispatch(Actions.GET_PROVEEDOR)
                .then(() => {
                    loading = ref(false);
                })
                .catch(() => {
                    const [error] = Object.keys(
                        store.getters.getProveedorErrors
                    );
                });
        };

        onMounted(() => {
            setCurrentPageBreadcrumbs("Modificar Datos", [
                "Páginas",
                "Dashboard",
            ]);
            getProveedor();
            store
                .dispatch(
                    Actions.GET_USUARIO,
                    store.getters.currentUser.tokenUsuario
                )
                .then(() => {
                    loading = ref(false);
                    console.log(store.getters.currentUsuario);
                    profileDetails.value = store.getters.currentUsuario;
                })
                .catch(() => {
                    const [error] = Object.keys(store.getters.getUsuarioErrors);
                });

            getBancos();
        });
        const profileDetails = ref<ProfileDetails>({
            tokenUsuario: store.getters.currentUsuario.tokenUsuario,
            nombres: store.getters.currentUsuario.nombres,
            apellidos: store.getters.currentUsuario.apellidos,
            email: store.getters.currentUsuario.email,
            telefono: store.getters.currentUsuario.telefono,
            bancoId: store.getters.currentUsuario.bancoId,
            nombreBanco: store.getters.currentUsuario.nombreBanco,
            tipoCuenta: store.getters.currentUsuario.tipoCuenta,
            numeroCuenta: store.getters.currentUsuario.numeroCuenta,
        });
        const profileSecurity = ref<ProfileSecurity>({
            tokenUsuario: store.getters.currentUsuario.tokenUsuario,
            contrasena: "",
            nuevaContrasena: "",
            confirmacionNuevaContrasena: "",
        });
        let loading = ref(true);

        return {
            submitButton1,
            submitButton2,
            submitButton3,
            submitButton4,
            submitButton5,
            submitButtonDatosBancarios,
            saveChanges1,
            profileDetails,
            profileSecurity,
            emailFormDisplay,
            passwordFormDisplay,
            profileDetailsValidator,
            changeEmail,
            changePassword,
            updateEmailButton,
            updatePasswordButton,
            updateEmail,
            updatePassword,
            bancos,
            datosProveedor,
            saveDatosBancarios,
            cargarDocumento,
            documento,
            descargarDocumento,
            enviarDocumento,
            submitButtonEnviarDocumentoBanco,
            confirmarSaveDatosBancarios
        };
    },
});
